import React, { useEffect, useState } from 'react';
import { Button, message } from 'antd';
import { DownloadOutlined, CloseOutlined } from '@ant-design/icons';
import { useAuth } from '../AuthContext';
import { getConsent } from './CookieConsent';

const DISMISS_KEY = 'pwa_install_dismissed_at';
const DISMISS_DAYS = 14; // re-show after 2 weeks if the user dismissed it

export default function InstallPWAPrompt({ lang: langProp }) {
  const [deferredEvt, setDeferredEvt] = useState(null);
  const [visible, setVisible] = useState(false);
  const auth = useAuth() || {};
  // AuthContext does not carry the language — take it from the prop, then the
  // <html lang> the app sets, and only then fall back to Arabic.
  const lang = langProp || auth.lang || document.documentElement.lang || 'ar';
  const ar = lang !== 'en';

  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone === true) return undefined;

    const dismissedAt = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
    if (dismissedAt && (Date.now() - dismissedAt) < DISMISS_DAYS * 24 * 60 * 60 * 1000) return undefined;

    // Never stack on top of the cookie banner — wait until that choice is made.
    const show = () => { if (getConsent()) setVisible(true); };

    const handler = (e) => {
      e.preventDefault();
      setDeferredEvt(e);
      setTimeout(show, 8000);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // iOS never fires beforeinstallprompt — offer the manual instruction instead.
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    if (isIOS && !window.navigator.standalone) setTimeout(show, 12000);

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  const install = async () => {
    if (!deferredEvt) {
      message.info(ar ? 'افتح قائمة المشاركة في Safari ثم اختر «إضافة إلى الشاشة الرئيسية»'
                      : 'In Safari, tap Share then "Add to Home Screen"', 8);
      dismiss();
      return;
    }
    deferredEvt.prompt();
    const choice = await deferredEvt.userChoice;
    if (choice.outcome === 'accepted') message.success(ar ? 'تم التثبيت' : 'Installed');
    setDeferredEvt(null);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="pwa-prompt" role="dialog" dir={ar ? 'rtl' : 'ltr'}
         aria-label={ar ? 'تثبيت تطبيق لانسر سيتي' : 'Install LancerCity'}>
      <img src="/logo192-maskable-v5.png" alt="" width="44" height="44" />
      <div className="pwa-prompt-text">
        <b>{ar ? 'ثبّت تطبيق لانسر سيتي' : 'Install LancerCity'}</b>
        <span>{ar ? 'وصول أسرع، إشعارات فورية، وتجربة بملء الشاشة' : 'Faster access, notifications, full-screen experience'}</span>
      </div>
      <Button type="primary" size="middle" icon={<DownloadOutlined />} onClick={install}>
        {ar ? 'تثبيت' : 'Install'}
      </Button>
      <Button type="text" size="small" icon={<CloseOutlined />} onClick={dismiss}
              aria-label={ar ? 'إغلاق' : 'Dismiss'} />
    </div>
  );
}
